import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
// Fonts are bundled (no third-party requests): EB Garamond for display, Poppins for UI (docs/DESIGN.md §2).
import "@fontsource/eb-garamond/latin-700.css";
import "@fontsource/eb-garamond/latin-800.css";
import "@fontsource/poppins/latin-400.css";
import "@fontsource/poppins/latin-500.css";
import "@fontsource/poppins/latin-600.css";
import "@fontsource/caveat/latin-600.css";
import "@fontsource/poppins/latin-700.css";
import { App } from "./App.tsx";
import { captureAppEmail } from "./lib/appLead.ts";
import { captureLeadCode } from "./lib/leadCode.ts";
import { captureRef } from "./lib/referrals.ts";
import { captureHave, withoutHaveParams } from "./lib/haveIntent.ts";
import { captureGo, withoutGoParams } from "./lib/goIntent.ts";
import { noteViaApp } from "./lib/storeApp.ts";
import { captureMessageOpen, withoutMessageParam } from "./lib/messageOpen.ts";
import { config } from "./env.ts";
import { captureAttribution } from "./lib/siteAnalytics.ts";
import { captureBuy, withoutBuyParams } from "./lib/buyIntent.ts";
import { captureDemoCarry } from "./lib/demoCarry.ts";
import { captureLevel, withoutLevelParam } from "./lib/levelIntent.ts";
import { captureVote } from "./lib/voteIntent.ts";
import { captureFix, withoutFixParams } from "./lib/fixIntent.ts";
import { captureRead, withoutReadParam } from "./lib/readIntent.ts";
import "./styles.css";
import { installCrashHook } from "./lib/crashHook.ts";

// The crash log: window errors and unhandled rejections go to POST /client-errors (lib/crashHook.ts, sender loaded only on an error).
installCrashHook("web");

// Opened from the phone app's in-app browser (?via=app): this tab never shows a card ask or checkout (lib/storeApp.ts).
noteViaApp(window.location.search);

// A visit from a referral link (cleancleaver.com/r/…) lands here with ?ref=: keep it until setup is done.
captureRef(window.location.search);
// A visit from a checklist email (…?signup=1&lead=CC10-…) keeps its code until the plan starts (lib/leadCode.ts).
captureLeadCode(window.location.search);

// A visit from "We have this" on a how-long entry or library guide (…?signup=1&have=croissant&where=display) keeps the intent
// until the app has a place to put it (lib/haveIntent.ts, screens/HaveArrive.tsx). It is taken off the address so a reload doesn't repeat it.
captureAttribution(window.location.search); // the site's sign-up link: how the reader came, as a bucket (never an id)
if (captureHave(window.location.search)) {
  try { window.history.replaceState(window.history.state, "", `${window.location.pathname}${withoutHaveParams(window.location.search)}${window.location.hash}`); } catch { /* the intent is already kept */ }
}

// A guided link from the CleanCleaver team (…?go=add_person&gm=…; lib/goIntent.ts): kept until the person is in, then opened once.
if (captureGo(window.location.search)) {
  try { window.history.replaceState(window.history.state, "", `${window.location.pathname}${withoutGoParams(window.location.search)}${window.location.hash}`); } catch { /* the link is already kept */ }
}
// A tapped reminder (browser push) carries ?mid=<send id>: counted once, then taken off the address (lib/messageOpen.ts).
if (captureMessageOpen(window.location.search, config.apiUrl)) {
  try { window.history.replaceState(window.history.state, "", `${window.location.pathname}${withoutMessageParam(window.location.search)}${window.location.hash}`); } catch { /* counted already */ }
}
// A buy button on the site (…?signup=1&buy=1&plan=team&state=UT&src=pricing, core buyintent.ts) keeps its intent through the sign-in
// (lib/buyIntent.ts): a short "Set up to buy" step, then Stripe. Off the address once kept, so a reload doesn't start it again.
const buy = captureBuy(window.location.search);
if (buy) {
  try { window.history.replaceState(window.history.state, "", `${window.location.pathname}${withoutBuyParams(window.location.search)}${window.location.hash}`); } catch { /* the intent is already kept */ }
}
// A sign-up from the sample café (…&from=demo&tried=checks,binder) keeps what the visitor tried for their own setup (lib/demoCarry.ts).
captureDemoCarry(window.location.search);
// A level card on the site (…?signup=1&level=standard, core cadence.ts levelFromQuery) keeps the level for setup (lib/levelIntent.ts).
if (captureLevel(window.location.search)) {
  try { window.history.replaceState(window.history.state, "", `${window.location.pathname}${withoutLevelParam(window.location.search)}${window.location.hash}`); } catch { /* the level is already kept */ }
}

// "Fix it" on the site's inspection lookup (…?signup=1&place=<key>&fix=<item>, core fixit.ts) keeps the place and the item (lib/fixIntent.ts).
if (captureFix(window.location.search)) {
  try { window.history.replaceState(window.history.state, "", `${window.location.pathname}${withoutFixParams(window.location.search)}${window.location.hash}`); } catch { /* the intent is already kept */ }
}

// A customer's answer under an item on a place page (…/app/vote?place=…&item=…&kind=…&v=…, core place-votes.ts): kept until they are signed in (lib/voteIntent.ts).
if (captureVote(window.location.pathname, window.location.search)) {
  try { window.history.replaceState(window.history.state, "", `${import.meta.env.BASE_URL.replace(/\/$/, "") || "/"}/`); } catch { /* the answer is already kept */ }
}

// A guide's or an article's sign-up button (…?signup=1&read=lib:sick-workers, core readfrom.ts): the sign-up screen names what they read.
if (captureRead(window.location.search)) {
  try { window.history.replaceState(window.history.state, "", `${window.location.pathname}${withoutReadParam(window.location.search)}${window.location.hash}`); } catch { /* the value is already kept */ }
}

// A visit from cleancleaver.com/get/ (a PDF's QR) brings the email the person typed in the address FRAGMENT (#email=…): keep it for this
// tab, take it off the address, and the email sign-in starts with it typed (lib/appLead.ts).
if (captureAppEmail(window.location.hash)) {
  try { window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}`); } catch { /* the email is already kept */ }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, "") || "/"}>
      <App />
    </BrowserRouter>
  </StrictMode>,
);
